import { DataLayers } from '@core/models/dataLayers/dataLayers.models';
import { Reports } from '@core/models/reports/reports.models';
import { AnomalyType } from '@hooks/queries/anomalyDetection/useAnomalyFeedback';
import type { DashboardIndex, PortfolioStatus } from '@hooks/queries/dashboard/dashboard.models';
import type { SeverityCategory } from '@hooks/queries/nema/nema.severity';
import type {
  NdviThresholdDirection,
  NdviThresholdScenario,
} from '@hooks/queries/notifications/userNotifications.models';
import { ExporterType } from '@hooks/queries/prescriptions/prescriptions.utils';
import type { SoilDataSource } from '@hooks/queries/soilData/useSoilData.models';
import type { AmplitudePage } from '@hooks/useCurrentPage';
import type { ReportFormat } from '@screens/Dashboard/report/reportGeneration.models';
import type { PortfolioColumnId } from '@screens/Dashboard/widgets/Portfolio/portfolioTable.models';
import {
  DataLayerStateKey,
  DataType,
  SatelliteImagesOption,
} from '@screens/DataLayers/dataLayer.store';
import type {
  AiConfidenceLevel,
  AiConfidenceSkill,
} from '@screens/Map/components/Overlays/AiInsights/AiConfidence/aiConfidence';
import { ChartView } from '@screens/Map/components/Overlays/TrialingPlotDrawer/TrialingPlotCharts/trialingPlotCharts.constants';
import { PrescriptionType } from '@screens/Prescriptions/utils/prescriptions.models';
import type { ExportFileType } from '@screens/ProductivityMaps/Create/ExportButton/ExportButton';
import {
  YieldByOptionValue,
  YieldListSortOrder,
} from '@screens/YieldAnalysis/YieldAnalysis.models';
import * as YieldAnalysisListUtils from '@screens/YieldAnalysis/components/YieldAnalysisListSection/YieldAnalysisListComponents/yieldAnalysisLists.utils';
import type { UnitSystem } from '@utils/unitUtils';
import dayjs from 'dayjs';
import { trackAnalyticsEvent } from 'src/analytics/analyticsLifecycle';
import { getAnalyticsEventContext } from 'src/analytics/eventContext';
import { CloudOptions, Source } from '../core/models/imageAssets';

interface ReportEventProperties {
  field_count?: number;
  source?: Source;
  max_cloud_coverage?: number;
  date_range_count?: number;
}

/** Where an image index or source was changed, so Map and Analytics stay distinguishable. */
export type ImageryChangeLocation = 'Map' | 'Histogram' | 'Time Series';

/** The charts stacked in the time series panel. */
export type TimeSeriesChart = 'ndvi' | 'variability' | 'precipitation';

export interface FieldSnapshotEventProperties {
  property_id?: string;
  field_count?: number | null;
}

interface TimeSeriesChartEventProperties {
  chart: TimeSeriesChart;
  show_precipitation: boolean;
}

/** The Dashboard is mounted under both the workspace and the property routes. */
export type DashboardLevel = 'workspace' | 'property';

export type DashboardFilter = 'reference_date' | 'entity' | 'crop' | 'growth_stage' | 'status';

export type DashboardResettableFilter = Exclude<DashboardFilter, 'reference_date'>;

export type DashboardRetryWidget = 'page' | 'growth_by_ndvi' | 'portfolio';

export type PlantingDateState = 'empty' | 'predicted' | 'registered';

interface DashboardEventProperties {
  level: DashboardLevel;
}

export interface DashboardApplyFilterEventProperties extends DashboardEventProperties {
  filter: DashboardFilter;
  active_filters_count: number;
  selected_count?: number;
  options_count?: number;
  days_from_today?: number;
  crop_name?: string | null;
  selected_stages?: readonly string[];
  statuses?: readonly PortfolioStatus[];
}

interface NdviThresholdEventProperties {
  scenario?: NdviThresholdScenario | null;
  direction?: NdviThresholdDirection | null;
  threshold?: number | null;
  fields_count?: number | null;
  farms_count?: number | null;
}

export function sendEvent(
  eventInput: string,
  eventProperties?: Record<string, unknown> | undefined
) {
  return trackAnalyticsEvent(eventInput, {
    ...getAnalyticsEventContext(),
    ...eventProperties,
  });
}

export const Amplitude = {
  trackSelectedSource(source: Source, location: ImageryChangeLocation) {
    sendEvent('[Explorer] Select Source', { source, location });
  },

  // ORGANIZATION
  trackOrgSelected: (org: { id: string; name: string }) => {
    sendEvent('Org Selected', { org_name: org.name, org_id: org.id });
  },

  //NAV BAR
  trackHelpButton: (type?: string) => {
    sendEvent('Resource Center Guides', { type });
  },

  //IMAGES
  trackSelectImage: (eventObject: { method: string; screen: string }) => {
    sendEvent('Select Image', eventObject);
  },

  //SIDE BAR
  trackSidebarClick: (amplitudeName: string) => {
    sendEvent('Click Sidebar Item', { page: amplitudeName });
  },

  //MAP VIEW
  trackVisualSlider: (positionPercentage: number) => {
    sendEvent('Visual Slider Used', { positionPercentage });
  },

  trackToggleViewMode: (
    viewMode: 'Variability' | 'Contrast',
    mode: 'on' | 'off',
    isFarmLevel: boolean
  ) => {
    sendEvent(`${viewMode} view toggled`, { mode, isFarmLevel });
  },

  trackIndex: (
    index: string,
    location: ImageryChangeLocation,
    properties?: { soil_datasource?: SoilDataSource }
  ) => {
    sendEvent('[Explorer] Change Index', { index, location, ...properties });
  },

  trackViewChange: (view) => {
    sendEvent('Change Map View', { view });
  },

  // Passed by reference in SceneList's onClick — looks dead but isn't.
  trackMapFilter: () => {
    sendEvent('Map Filter');
  },

  trackMapDateRange: (start: Date, end: Date) => {
    const sDate = dayjs(start);
    const eDate = dayjs(end);
    sendEvent('Change Asset Date Range', {
      'Start Date': sDate.format('DD-MM-YYYY'),
      'End Date': eDate.format('DD-MM-YYYY'),
      Duration: eDate.diff(sDate, 'd'),
    });
  },

  trackMapCloudOptions: (options: Array<CloudOptions>) => {
    sendEvent('Change Cloud Filter', {
      Sunny: options.includes(CloudOptions.sunny),
      'Partly Cloudy': options.includes(CloudOptions.sunny_cloudless),
      Cloudy: options.includes(CloudOptions.overcast),
    });
  },

  trackTools: (tool: string, type: string) => {
    sendEvent('Tools', { tool, type });
  },

  trackFilterSources: (active: string, disable: string) => {
    sendEvent('Map Image Source Filter', { active, disable });
  },

  trackMapFieldSearchSelectField: ({
    fieldId,
    regionId,
  }: {
    fieldId: string;
    regionId: string;
  }) => {
    sendEvent('[Map Field Search] Field Selected', { fieldId, regionId });
  },

  trackTileInitialLoading: (properties: {
    method: string;
    time: number;
    scenesLoadTime: number;
    tilesLoadTime: number;
  }) => {
    sendEvent('Initial Loading', properties);
  },

  trackTileSelectionLoading: (properties: { method: string; time: number }) => {
    sendEvent('Selection Loading', properties);
  },

  //PRESCRIPTIONS
  trackStartNewPrescription: () => {
    sendEvent('Start New Prescription');
  },

  trackFinishNewPrescription: (
    type: string,
    fields_quantity: number,
    products_quantity: number,
    products_names: Array<string>,
    zones_number?: number
  ) => {
    sendEvent('Save New Prescription', {
      type,
      fields_quantity,
      products_quantity,
      products_names,
      zones_number,
    });
  },

  trackFinishEditPrescription: (type) => {
    sendEvent('Edit a prescription', { type });
  },

  trackPrescriptionDownload: (type, output: ExporterType) => {
    sendEvent('Download Prescription', { type, output });
  },

  trackDeletePrescription: (type) => {
    sendEvent('Delete Prescription', { type });
  },

  trackDuplicatePrescription: () => {
    sendEvent('Duplicate as a Copy Prescription');
  },

  trackDuplicateEditingPrescription: () => {
    sendEvent('Duplicate Editing Prescription');
  },

  trackZoneMethod: (type: string, selected_palette?: string) => {
    sendEvent('Prescription Zone Creation Method', { type, selected_palette });
  },

  trackHandDrawTools: (selected_tool: string) => {
    sendEvent('Prescription Hand Draw Tools', { selected_tool });
  },

  trackZonesMerge: (selected_action: string, merged_zones_quantity?: number) => {
    sendEvent('Prescription Zone Merge', { selected_action, merged_zones_quantity });
  },

  trackLayerChange: (selected_layer: string) => {
    sendEvent('Prescription Layer Change', { selected_layer });
  },

  trackNavigationBreadcrumb: (from: string, to: string) => {
    sendEvent('Prescription Breadcrumb Navigation', { from, to });
  },

  trackPrescriptionEditReview: (option: 'Type and Location' | 'Zones' | 'Products') => {
    sendEvent('[Prescriptions] Edit data at Review Step', { option });
  },

  //REPORTS
  trackGenerateReportRequest: (type: Reports.ReportTypes, properties?: ReportEventProperties) => {
    sendEvent('Requested to generate report', { type, ...properties });
  },

  trackDownloadReport: (type: Reports.ReportTypes, properties?: ReportEventProperties) => {
    sendEvent('Clicked to download report', { type, ...properties });
  },

  trackWeeklyReportGenerate: (
    fields_quantity: number,
    farms_quantity: number,
    source: string,
    total_area_ha: number,
    max_cloud_coverage: number
  ) => {
    sendEvent('[Weekly Report] Generate Weekly Report', {
      fields_quantity,
      farms_quantity,
      source,
      total_area_ha,
      max_cloud_coverage,
    });
  },

  trackWeeklyReportSelectAllFarms: (selection: 'Select All' | 'Unselect All') => {
    sendEvent('[Weekly Report] Select all farms', { selection });
  },

  trackWeeklyReportSelectAllFields: (selection: 'Select All' | 'Unselect All') => {
    sendEvent('[Weekly Report] Select all fields', { selection });
  },

  trackWeeklyReportToggleCloudCoverage: (activate: boolean) => {
    sendEvent('[Weekly Report] Toggle Field Cloud Coverage', { activate });
  },

  trackWeeklyReportClearSelection: () => {
    sendEvent('[Weekly Report] Clear Selection');
  },

  //NOTES
  trackStartNewNote: () => {
    sendEvent('Start New Note');
  },

  trackFinishNewNote: (tag, field, images) => {
    sendEvent('Save New Note', { tag, field, images });
  },

  trackUpdateNote: ({ oldTags, newTags }: { oldTags: string[]; newTags: string[] }) => {
    sendEvent('Update Note', { oldTags, newTags });
  },

  trackDeleteNote: (tag: string[], attachments: string[]) => {
    sendEvent('Delete Note', { tag, attachments });
  },

  trackFilterNote: (tags, fields) => {
    sendEvent('Filter Notes', { tags, fields });
  },

  //FIELDS
  trackCreateNewField: () => sendEvent('Create Field'),

  trackSelectedField: ({
    selected,
    contrastEnabled,
    trialPlotsEnabled,
    isSideMap,
    page,
  }: {
    selected: boolean;
    contrastEnabled: boolean;
    trialPlotsEnabled: boolean;
    isSideMap: boolean;
    page?: AmplitudePage;
  }) => {
    sendEvent('Select Field on Map', {
      selected,
      contrastEnabled,
      trialPlotsEnabled,
      isSideMap,
      page,
    });
  },

  //ANALYTICS
  trackHistogramSort: (type) => {
    sendEvent('[Histogram] Sort', { type });
  },

  trackHistogramDownload: (type: string) => {
    sendEvent('[Histogram] Download', { type });
  },

  trackTimeSeriesDownload: (
    type: 'CSV Report' | 'PDF Report',
    properties: { precipitation_included: boolean }
  ) => {
    sendEvent('[Time Series] Download', { type, ...properties });
  },

  trackFieldSnapshot: (properties: FieldSnapshotEventProperties) => {
    sendEvent('[Field Snapshot] Open', { ...properties });
  },

  trackFieldSnapshotDownload: (properties: FieldSnapshotEventProperties) => {
    sendEvent('[Field Snapshot] Download', { ...properties });
  },

  trackSideBySide: (on) => {
    sendEvent('[Map View] Toggle Side-by-Side', { on });
  },

  trackAnalyticsView: (view) => {
    sendEvent('[Analytics] Change View', { view });
  },

  // TIME SERIES
  trackTimeSeriesTogglePrecipitation: (properties: {
    enabled: boolean;
    date_ranges_count: number;
    unit_system: UnitSystem;
    source: Source;
  }) => {
    sendEvent('[Time Series] Toggle Precipitation', { ...properties });
  },

  trackTimeSeriesToggleChartSection: (
    properties: TimeSeriesChartEventProperties & { action: 'Expand' | 'Collapse' }
  ) => {
    sendEvent('[Time Series] Toggle Chart Section', { ...properties });
  },

  trackTimeSeriesRetryChart: (properties: TimeSeriesChartEventProperties) => {
    sendEvent('[Time Series] Retry Chart', { ...properties });
  },

  trackTimeSeriesChangeDateRanges: (properties: {
    action: 'Add' | 'Remove' | 'Edit';
    ranges_count: number;
    show_precipitation: boolean;
  }) => {
    sendEvent('[Time Series] Change Date Ranges', { ...properties });
  },

  trackTimeSeriesSelectPrecipitationRange: (properties: {
    range_index: number;
    ranges_count: number;
  }) => {
    sendEvent('[Time Series] Select Precipitation Range', { ...properties });
  },

  trackTimeSeriesChangeCloudCoverage: (cloud_coverage: number) => {
    sendEvent('[Time Series] Change Cloud Coverage', { cloud_coverage });
  },

  trackTimeSeriesSelectField: (properties: { fields_available: number; searched: boolean }) => {
    sendEvent('[Time Series] Select Field', { ...properties });
  },

  // DASHBOARD
  trackDashboardApplyFilter: (properties: DashboardApplyFilterEventProperties) => {
    sendEvent('[Dashboard] Apply Filter', { ...properties });
  },

  trackDashboardResetFilter: (
    properties: DashboardEventProperties & { filter: DashboardResettableFilter }
  ) => {
    sendEvent('[Dashboard] Reset Filter', { ...properties });
  },

  trackDashboardSearchPortfolio: (
    properties: DashboardEventProperties & { query_length: number; results_count: number }
  ) => {
    sendEvent('[Dashboard] Search Portfolio', { ...properties });
  },

  trackDashboardSortTable: (
    properties: DashboardEventProperties & { column: PortfolioColumnId; direction: 'asc' | 'desc' }
  ) => {
    sendEvent('[Dashboard] Sort Table', { ...properties });
  },

  trackDashboardEditPlantingDate: (
    properties: DashboardEventProperties & { state: PlantingDateState }
  ) => {
    sendEvent('[Dashboard] Edit Planting Date', { ...properties });
  },

  trackDashboardToggleCropSection: (
    properties: DashboardEventProperties & {
      action: 'Expand' | 'Collapse';
      crop_name: string;
      rows_count: number;
    }
  ) => {
    sendEvent('[Dashboard] Toggle Crop Section', { ...properties });
  },

  trackDashboardChangeNdviCrop: (
    properties: DashboardEventProperties & {
      control: 'arrow' | 'dot';
      crop_name: string;
      crops_count: number;
    }
  ) => {
    sendEvent('[Dashboard] Change NDVI Crop', { ...properties });
  },

  trackDashboardChangeIndex: (properties: DashboardEventProperties & { index: DashboardIndex }) => {
    sendEvent('[Dashboard] Change Index', { ...properties });
  },

  trackDashboardStartReport: (
    properties: DashboardEventProperties & {
      format: ReportFormat;
      rows_count: number;
      is_filtered: boolean;
    }
  ) => {
    sendEvent('[Dashboard] Start Report', { ...properties });
  },

  trackDashboardDownloadReport: (
    properties: DashboardEventProperties & { format: ReportFormat; rows_count: number }
  ) => {
    sendEvent('[Dashboard] Download Report', { ...properties });
  },

  trackDashboardCancelReport: (
    properties: DashboardEventProperties & { format: ReportFormat; step: 'running' | 'review' }
  ) => {
    sendEvent('[Dashboard] Cancel Report', { ...properties });
  },

  trackDashboardRetryLoad: (
    properties: DashboardEventProperties & { widget: DashboardRetryWidget }
  ) => {
    sendEvent('[Dashboard] Retry Load', { ...properties });
  },

  // DRONE UPLOADS
  trackOpenDrawer: () => {
    sendEvent('Drone Upload Icon Click');
  },

  trackDroneImageUpload: () => {
    sendEvent('Drone Upload Image Button Select');
  },

  trackDroneToggle: () => {
    sendEvent('Show Drone Toggle');
  },

  trackDroneUploadSuccessful: () => {
    sendEvent('Upload Drone Imagery');
  },

  // EASY SCOUT
  trackEasyScoutHover: () => {
    sendEvent('Easy Scout Hover');
  },

  trackEasyScoutCollapse: (type) => {
    sendEvent('Easy Scout Collapse', { type });
  },

  trackEasyScoutFilterClassification: (type) => {
    sendEvent('Easy Scout Filter Classification', { type });
  },

  trackEasyScoutFilterSource: (type) => {
    sendEvent('Easy Scout Filter Source', { type });
  },

  trackEasyScoutScroll: () => {
    sendEvent('Easy Scout Scroll');
  },

  trackEasyScoutSelectField: () => {
    sendEvent('Easy Scout Select Field');
  },

  // FIELD BOUNDARY MENU
  trackFieldMenuActionsOnField: (type) => {
    sendEvent('Field Menu Actions on Field', { type });
  },

  trackFieldMenuActionsOnMap: (type: string) => {
    sendEvent('Field Menu Actions off Field', { type });
  },

  // PROPERTY AND FIELD IMAGE DOWNLOAD
  trackImageDownload: (location: string, download_type: string, level: string) => {
    sendEvent('Download Image', { location, download_type, level });
  },

  // PRODUCTIVITY MAP
  trackProductivityMapCreated: () => {
    sendEvent('Productivity Map Created');
  },

  trackProductivityMapDownloaded: (fileFormat: string) => {
    sendEvent('Productivity Map Downloaded', { file_type: fileFormat });
  },

  trackProductivityMapSaved: (name: string) => {
    sendEvent('Productivity Map Saved', { name });
  },

  trackProductivityMapRedownloaded: (format: ExportFileType) => {
    sendEvent('Productivity Map Redownloaded', { file_type: format });
  },

  // ANOMALY DETECTION
  trackAnomalyDetectionCalloutAction: (action: 'Show' | 'DontShowAgain') => {
    sendEvent('[Anomaly Detection] Callout Action', { action });
  },

  trackAnomalyDetectionFeedback: (isAnomaly: boolean, type: AnomalyType | undefined) => {
    sendEvent('Anomaly Feedback Submitted', { isAnomaly, type });
  },

  trackAnomalyDetectionIgnore: (isIgnore: boolean) => {
    sendEvent('Ignore Anomaly', { isIgnore: isIgnore });
  },

  trackAnomalyDetectionCreateNote: () => {
    sendEvent('Create Anomaly Note');
  },

  trackAnomalyDetectionCreateTask: () => {
    sendEvent('Create Anomaly Task');
  },

  trackAnomalyActivationToggle: (is_active: boolean) => {
    sendEvent('[Anomaly detection] Activation Toggle', { is_active: is_active });
  },

  trackAnomalyChangeScenePopup: (change_scene: boolean) => {
    const popoverDismissed = JSON.parse(
      localStorage.getItem('anomaly-detection-change-scene-popover-dismissed') ?? 'null'
    );
    sendEvent('[Anomaly Detection] Change Scene Popup', {
      dont_display_again: Boolean(popoverDismissed),
      change_scene: change_scene,
    });
  },

  trackAnomalyGenerateBaseline: (
    fields_quantity: number,
    is_multiple_selection: boolean,
    has_baseline_error: boolean
  ) => {
    sendEvent('[Anomaly Detection] Generate Baseline', {
      fields_quantity: fields_quantity,
      is_multiple_selection: is_multiple_selection,
      has_baseline_error: has_baseline_error,
    });
  },

  trackAnomalyAccessSettings: () => {
    sendEvent('[Anomaly Detection] Access Settings');
  },

  trackAnomalyAccessLearnMore: (location: 'Map' | 'Comparison View') => {
    sendEvent('[Anomaly Detection] Access Learn More', { location: location });
  },

  trackAnomalyClick: (
    is_ignored: boolean,
    location: 'Map' | 'Comparison View List' | 'Comparison View Map'
  ) => {
    sendEvent('[Anomaly Detection] Click Anomaly', {
      is_ignored: is_ignored,
      location: location,
    });
  },

  trackAnomalyComparisonFieldClick: (anomalies_quantity: number) => {
    sendEvent('[Anomaly Detection] Click Field on Comparison View', {
      anomalies_quantity: anomalies_quantity,
    });
  },

  trackAnomalyDownloadBaseline: () => {
    sendEvent('[Anomaly Detection] Download Baseline File');
  },

  trackAnomalySettingsFieldSelect: (location: 'map' | 'list', selected: boolean) => {
    sendEvent('[Anomaly Detection] Select Field on Settings', { location, selected });
  },

  trackAnomalySettingsCancel: () => {
    sendEvent('[Anomaly Detection] Cancel settings changes');
  },

  trackAnomalySettingsRetryBaselines: () => {
    sendEvent('[Anomaly Detection] Retry loading Baselines');
  },

  trackAnomalySettingsSave: (
    fieldQuantity: number,
    cropsAssigned: ('soybean' | 'corn' | 'cotton')[]
  ) => {
    sendEvent('[Anomaly Detection] Save settings changes', {
      fieldQuantity,
      cropsAssigned,
    });
  },

  // PALETTE CHANGE
  trackChangeSelectedPalette: (paletteName: string, isContrast: boolean) => {
    sendEvent('Change Selected Palette', {
      paletteName: paletteName,
      isContrast: isContrast,
    });
  },

  // YIELD ANALYSIS
  trackYieldAccessOverview: (fieldSelected: boolean) => {
    sendEvent('[Yield Analysis] Access Overview', {
      fieldSelected: fieldSelected,
    });
  },

  trackYieldCsvDownload: (
    firstAttribute: string,
    secondAttribute: string,
    fieldSelected: boolean,
    location: string
  ) => {
    sendEvent('[Yield Analysis] CSV Download', {
      firstAttribute: firstAttribute,
      secondAttribute: secondAttribute,
      fieldSelected: fieldSelected,
      location: location,
    });
  },

  trackYieldChangeSelectedOption: (selectedOption: YieldByOptionValue, fieldSelected: boolean) => {
    sendEvent('[Yield Analysis] Change Drawer Selected Option', {
      selectedOption: selectedOption,
      fieldSelected: fieldSelected,
    });
  },

  trackYieldChangeFilterOption: (option: YieldAnalysisListUtils.ListFilter) => {
    sendEvent('[Yield Analysis] Change Drawer Filter Option', {
      option: option ?? 'none',
    });
  },

  trackYieldByOptionSorting: (
    sortOrder: YieldListSortOrder,
    selectedOption: YieldByOptionValue,
    fieldSelected: boolean
  ) => {
    sendEvent('[Yield Analysis] Yield By Option Sorting', {
      sortOrder: sortOrder,
      selectedOption: selectedOption,
      fieldSelected: fieldSelected,
    });
  },

  trackYieldOverviewTableCombination: (
    firstAttribute: YieldByOptionValue,
    secondAttribute: YieldByOptionValue,
    fieldSelected: boolean
  ) => {
    sendEvent('[Yield Analysis] Overview table combination', {
      firstAttribute: firstAttribute,
      secondAttribute: secondAttribute,
      combination: `${firstAttribute} x ${secondAttribute}`,
      fieldSelected: fieldSelected,
    });
  },

  trackYieldSelectField: (location: string, hasManagementZones: boolean) => {
    sendEvent('[Yield Analysis] Select Field', {
      location: location,
      hasManagementZones: hasManagementZones,
    });
  },

  trackYieldEmptyStateAction: (action: string) => {
    sendEvent('[Yield Analysis] Empty State Action', {
      action: action,
    });
  },

  trackYieldSelectManagementZone: (isValidationBlock: boolean) => {
    sendEvent('[Yield Analysis] Select Management Zone', {
      isValidationBlock: isValidationBlock,
    });
  },

  trackYieldManagementZoneChartType: (chartType: 'bar' | 'boxplot') => {
    sendEvent('[Yield Analysis] Management Zone Selected Chart Type', {
      chartType: chartType,
    });
  },

  trackYieldInitialLoading: (time: number, fieldsQuantity: number) => {
    sendEvent('[Yield Analysis] Initial Loading', {
      timeInSeconds: time,
      fieldsQuantity: fieldsQuantity,
    });
  },

  trackYieldSelectTrialingGroup: (action: 'Expand' | 'Collapse', plots_quantity: number) => {
    sendEvent('[Yield Analysis] Select Trial Plot Group', { action, plots_quantity });
  },

  trackYieldSelectTrialPlot: () => {
    sendEvent('[Yield Analysis] Select Trial Plot');
  },

  trackYieldToggleTrialPlotVisibility: (visible: boolean) => {
    sendEvent('[Yield Analysis] Toggle Trial Plot Visibility', { visible });
  },

  trackYieldToggleTrialPlotGroupVisibility: (visible: boolean) => {
    sendEvent('[Yield Analysis] Toggle Trial Plot Group Visibility', { visible });
  },

  trackEnableTrialingPlots: (value: boolean, side: 'map' | 'sideMap' | undefined) => {
    sendEvent('[Yield Analysis] Enable mapview trialing plots', {
      value,
      side: side ?? 'map',
    });
  },

  trackYieldSyncData: () => {
    sendEvent('[Yield Analysis] Click Sync Field Data Button');
  },

  // DATA LAYERS
  trackLayersAccessFromYieldAnalysis: () => {
    sendEvent('[Data Layers] Access from Yield Analysis');
  },

  trackLayersSelectField: (
    location: 'Map' | 'Sidebar Field List' | 'Map Field List',
    map_side: DataLayerStateKey | 'shared'
  ) => {
    sendEvent('[Data Layers] Select Field', {
      location: location,
      map_side: map_side,
    });
  },

  trackLayersSelectOperation: (
    operation_type: DataLayers.OperationType,
    map_side: DataLayerStateKey
  ) => {
    sendEvent('[Data Layers] Select Operation', {
      operation_type: operation_type,
      map_side: map_side,
    });
  },

  trackLayersScreenStates: (
    option:
      | 'Retry Loading Operations List'
      | 'Retry Loading Operation Details'
      | 'Retry Loading Nema Details'
      | 'Upload data'
  ) => {
    sendEvent('[Data Layers] Screen States', {
      option: option,
    });
  },

  trackLayersChangeOperationsFilter: (
    selected_options: DataLayers.OperationType[],
    map_side: DataLayerStateKey
  ) => {
    sendEvent('[Data Layers] Change Operations Filter', {
      selected_options: JSON.stringify(selected_options),
      map_side: map_side,
    });
  },

  trackLayersChangePrescriptionsFilter: (
    selected_options: PrescriptionType[],
    map_side: DataLayerStateKey
  ) => {
    sendEvent('[Data Layers] Change Prescriptions Filter', {
      selected_options: JSON.stringify(selected_options),
      map_side: map_side,
    });
  },

  trackLayersAccessHelpFile: () => {
    sendEvent('[Data Layers] Access Help File');
  },

  trackLayersOperationLegend: (
    map_side: DataLayerStateKey,
    histogram_enabled: boolean,
    legend_type: string,
    operation_type?: DataLayers.OperationType
  ) => {
    sendEvent('[Data Layers] Operation Legend', {
      map_side: map_side,
      histogram_enabled: histogram_enabled,
      legend_type: legend_type,
      operation_type: operation_type,
    });
  },

  trackLayersMapOptionVisibility: (is_open: boolean) => {
    sendEvent('[Data Layers] Map Option Menu Visibility ', { is_open: is_open });
  },

  trackLayersMapOptionSettings: (selected_option: 'One image' | 'Two images') => {
    sendEvent('[Data Layers] Map Option Menu Settings', {
      selected_option: selected_option,
    });
  },

  trackLayersMapOptionBackground: (selected_option: 'Vector map' | 'Satellite') => {
    sendEvent('[Data Layers] Map Option Menu Background', {
      selected_option: selected_option,
    });
  },

  trackLayersTypeSelect: ({
    map_side,
    type,
    opposite_type,
  }: {
    map_side: DataLayerStateKey;
    type: DataType;
    opposite_type?: DataType;
  }) => {
    sendEvent('[Data Layers] Type Select', {
      map_side,
      type,
      opposite_type,
    });
  },

  trackLayersSatelliteType: ({
    map_side,
    option,
  }: {
    map_side: DataLayerStateKey;
    option: SatelliteImagesOption;
  }) => {
    sendEvent('[Data Layers] Satellite Image Type Select', { map_side, option });
  },

  trackLayersOpenImagesFilter: ({ map_side }: { map_side: DataLayerStateKey }) => {
    sendEvent('[Data Layers] Open Images Filter', { map_side });
  },

  trackLayersSelectImage: ({
    map_side,
    type,
  }: {
    map_side: DataLayerStateKey;
    type: 'satellite' | 'drone';
  }) => {
    sendEvent('[Data Layers] Select Image', { map_side, type });
  },

  trackLayersImageLegend: ({
    map_side,
    histogram_enabled,
    image_type,
    legend_type,
  }: {
    map_side: DataLayerStateKey;
    histogram_enabled: boolean;
    image_type: string;
    legend_type: string;
  }) => {
    sendEvent('[Data Layers] Image Legend', {
      map_side,
      histogram_enabled,
      image_type,
      legend_type,
    });
  },

  trackLayersOpenContrastPalette: ({ map_side }: { map_side: DataLayerStateKey }) => {
    sendEvent('[Data Layers] Open Contrast Palette Selector', { map_side });
  },

  trackLayersMeasureNDVI: (map_side: DataLayerStateKey) => {
    sendEvent('[Data Layers] Measure NDVI', { map_side });
  },

  trackLayersMeasureNDVIDiscarded: (map_side: DataLayerStateKey) => {
    sendEvent('[Data Layers] Measure NDVI Discarded', { map_side });
  },

  /** User clicks on the field map boundary with the right button */
  trackLayersOpenedContextMenu: (map_side: DataLayerStateKey) => {
    sendEvent('[Data Layers] Opened Context Menu', { map_side });
  },

  trackLayersClickOperationsVariety: ({
    map_side,
    location,
    operationType,
  }: {
    map_side: DataLayerStateKey;
    location: 'Map' | 'Legend';
    operationType?: DataLayers.OperationType;
  }) => {
    sendEvent('[Data Layers] Click Operations Variety', { map_side, location, operationType });
  },

  trackLayersSelectPrescription: ({
    map_side,
    prescription_type,
  }: {
    map_side: DataLayerStateKey;
    prescription_type: PrescriptionType;
  }) => {
    sendEvent('[Data Layers] Select Prescription', { map_side, prescription_type });
  },

  trackLayersSelectNema: ({
    map_side,
    severity_category,
    all_damage,
    has_crop_season,
  }: {
    map_side: DataLayerStateKey;
    severity_category: SeverityCategory;
    all_damage: number;
    has_crop_season: boolean;
  }) => {
    sendEvent('[Data Layers] Select Nema Analysis', {
      map_side,
      severity_category,
      all_damage,
      has_crop_season,
    });
  },

  trackLayersSelectProductivityMap: ({
    map_side,
    images_quantity,
  }: {
    map_side: DataLayerStateKey;
    images_quantity: number;
  }) => {
    sendEvent('[Data Layers] Select Productivity Map', { map_side, images_quantity });
  },

  // AI INSIGHTS DRAWER
  trackAiInsightsDrawerOpen: () => {
    sendEvent('[AI Insights] Open Insights Drawer');
  },

  trackAiInsightsDrawerClose: () => {
    sendEvent('[AI Insights] Close Insights Drawer');
  },

  trackAiInsightsEditGrowthStage: () => {
    sendEvent('[AI Insights] Edit Crop Growth Stage');
  },

  trackAiInsightsConfirmGrowthStage: ({ crop_edited }: { crop_edited: boolean }) => {
    sendEvent('[AI Insights] Confirm Crop Growth Stage', { crop_edited });
  },

  trackAiInsightsDismissGrowthStage: () => {
    sendEvent('[AI Insights] Dismiss Confirmed Growth Stage');
  },

  trackAiInsightsConfidenceAssessment: ({
    skill,
    confidence,
  }: {
    skill: AiConfidenceSkill;
    confidence: AiConfidenceLevel;
  }) => {
    sendEvent('[AI Insights] Confidence Assessment', { skill, confidence });
  },

  trackAiInsightsOpenConfidenceAssessment: ({
    skill,
    confidence,
  }: {
    skill: AiConfidenceSkill;
    confidence: AiConfidenceLevel;
  }) => {
    sendEvent('[AI Insights] Open Confidence Assessment', { skill, confidence });
  },

  trackAiInsightsUserFeedback: ({ liked }: { liked: boolean }) => {
    sendEvent('[AI Insights] User feedback for generated insights', { liked });
  },

  trackAiInsightsCreateTask: () => {
    sendEvent('[AI Insights] Create Task');
  },

  trackAiInsightsCreateNote: () => {
    sendEvent('[AI Insights] Create Note');
  },

  trackAiChatbotOpen: () => {
    sendEvent('[AI Chatbot] Open Chatbot');
  },

  trackUserRedirect: ({ from, to }: { from: string; to: string }) => {
    sendEvent('[Rebrand] Redirect user', { from, to });
  },

  // TRIAL PLOTS
  trackTrialPlotClick: () => {
    sendEvent('[Trial Plots] Trial Plot Click');
  },

  trackTrialPlotDrawerClose: () => {
    sendEvent('[Trial Plots] Close Drawer');
  },

  trackTrialPlotOpenCharts: () => {
    sendEvent('[Trial Plots] Open Charts');
  },

  trackTrialPlotCloseCharts: (action: 'back' | 'close') => {
    sendEvent('[Trial Plots] Close Charts', { action });
  },

  trackTrialPlotSwitchChartType: (chart_type: ChartView) => {
    sendEvent('[Trial Plots] Switch Chart Type', { chart_type });
  },

  trackTrialPlotSortPlots: (sort_order: 'highest' | 'lowest' | 'default') => {
    sendEvent('[Trial Plots] Sort Plots', { sort_order });
  },

  trackTrialPlotTogglePlotVisibility: (visible: boolean) => {
    sendEvent('[Trial Plots] Toggle Plot Visibility', { visible });
  },

  trackTrialPlotChartsErrorRetry: () => {
    sendEvent('[Trial Plots] Charts Error Retry');
  },

  // MAPBIOMAS

  trackMapbiomasDownload: ({
    format,
    location,
    crop,
    isSideMap,
  }: {
    format: 'CSV' | 'PNG';
    location: 'Map' | 'Date Picker';
    crop: 'First' | 'Second';
    isSideMap: boolean;
  }) => {
    sendEvent('[Mapbiomas] Download Data', { format, location, crop, isSideMap });
  },

  trackMapbiomasChangeCrop: ({
    crop,
    isSideMap,
  }: {
    crop: 'First' | 'Second';
    isSideMap: boolean;
  }) => {
    sendEvent('[Mapbiomas] Change Crop', { crop, isSideMap });
  },

  trackMapbiomasChangeYear: ({ year, isSideMap }: { year: number; isSideMap: boolean }) => {
    sendEvent('[Mapbiomas] Change Year', { year, isSideMap });
  },

  trackMapbiomasRetry: ({
    location,
    crop,
    isSideMap,
  }: {
    location: 'Map' | 'Legend';
    crop: 'First' | 'Second';
    isSideMap: boolean;
  }) => {
    sendEvent('[Mapbiomas] Retry Loading Data', { location, crop, isSideMap });
  },

  // NOTIFICATIONS

  trackNdviThresholdGoToField: (properties: NdviThresholdEventProperties) => {
    sendEvent('[Notifications] NDVI Threshold Go to Field', { ...properties });
  },

  trackNdviThresholdGoToFarm: (properties: NdviThresholdEventProperties) => {
    sendEvent('[Notifications] NDVI Threshold Go to Farm', { ...properties });
  },

  trackNdviThresholdOpenReport: (properties: NdviThresholdEventProperties) => {
    sendEvent('[Notifications] NDVI Threshold Open Report', { ...properties });
  },

  trackNotificationsOpenCenter: () => {
    sendEvent('[Notifications] Open Center');
  },

  trackNotificationsMarkAllAsRead: () => {
    sendEvent('[Notifications] Mark All as Read');
  },

  trackNotificationsOpenSettings: () => {
    sendEvent('[Notifications] Open Settings');
  },

  trackNotificationsChangeTab: (tab: 'all' | 'unread') => {
    sendEvent('[Notifications] Change Tab', { tab });
  },

  trackNotificationsAnomalyChangeSettings: () => {
    sendEvent('[Notifications] Anomaly Change Settings');
  },

  trackNotificationsAnomalyShowMe: (destination: 'farm' | 'organization' | 'workspace') => {
    sendEvent('[Notifications] Anomaly Show Me', { destination });
  },

  trackNotificationsDownloadZipFile: (service: string) => {
    sendEvent('[Notifications] Download Zip File', { service });
  },

  trackNotificationsFieldInsightsEnable: () => {
    sendEvent('[Notifications] Field Insights Enable');
  },

  trackNotificationsFieldInsightsChangeSettings: () => {
    sendEvent('[Notifications] Field Insights Change Settings');
  },

  trackNotificationsWeeklyInsightsOpenReport: (with_ai_summary: boolean) => {
    sendEvent('[Notifications] Weekly Insights Open Report', { with_ai_summary });
  },
} as const;
